import type { PageCatchUpState, PageHead } from './syncEngine'
import type { YjsServerCursor } from '../platform/webStorage'
import { throwIfAborted } from './requestDeadline'
import { syncLog } from './syncLog'

const CONCURRENCY = 2

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export interface PageCatchUpHooks {
  ready: () => boolean
  workspaceId: () => Promise<string | null>
  readState: (workspaceId: string) => PageCatchUpState
  writeState: (workspaceId: string, state: PageCatchUpState) => void
  pullHeads: (workspaceId: string, after: number | null) => Promise<{ heads: PageHead[]; cursor: number }>
  /** Pages in the corpus, and the pages in its Trash. */
  pages: () => { live: Set<string>; trashed: Set<string> }
  /** The open page, which its own connection keeps current. */
  isOpen: (pageId: string) => boolean
  /** How far this device's copy of a page has caught up with its edit log. */
  localPosition: (pageId: string) => Promise<YjsServerCursor | null>
  catchUp: (pageId: string, signal: AbortSignal) => Promise<void>
  /** After a pass that caught up at least one page. */
  caughtUp: (pageIds: string[]) => Promise<void>
}

/**
 * Keeps pages that aren't open current. Each pass asks the server which pages'
 * edit logs moved since the last pass, and fetches only the edits this device
 * is missing, two pages at a time, without opening editors. See
 * docs/page-catch-up.md.
 */
export class BackgroundPageCatchUp {
  private running: Promise<void> | null = null
  private controller: AbortController | null = null
  private queued: { full: boolean } | null = null
  /** Whether a pass has finished this session, successfully or not. */
  private finishedPass = false

  constructor(private hooks: PageCatchUpHooks) {}

  /**
   * Waits until no pass is running or queued, and, while sync is available,
   * until this session's first pass has finished, so a backup started at
   * launch includes what that pass fetches. Gives up after `timeoutMs`;
   * true when settled.
   */
  async settled(timeoutMs: number): Promise<boolean> {
    const deadline = Date.now() + timeoutMs
    for (;;) {
      const awaitingFirstPass = !this.finishedPass && this.hooks.ready()
      if (!this.running && !this.queued && !awaitingFirstPass) return true
      const remaining = deadline - Date.now()
      if (remaining <= 0) return false
      // Between passes (or before the first starts) there is nothing to await, so check again shortly.
      await Promise.race([this.running ?? delay(Math.min(500, remaining)), delay(remaining)])
    }
  }

  stop(): void {
    this.queued = null
    this.controller?.abort()
  }

  /**
   * Starts a pass, or queues one more after the pass in progress. `full`
   * re-checks every page instead of only those changed since the last pass.
   */
  nudge(options: { full?: boolean } = {}): Promise<void> {
    if (this.running) {
      this.queued = { full: Boolean(options.full || this.queued?.full) }
      return this.running
    }
    if (!this.hooks.ready()) return Promise.resolve()
    const controller = new AbortController()
    this.controller = controller
    this.running = this.run(Boolean(options.full), controller.signal).catch((cause) => {
      if (!controller.signal.aborted) this.logFailure('Couldn’t bring closed pages up to date', cause)
    }).finally(() => {
      this.running = null
      this.controller = null
      this.finishedPass = true
      const queued = this.queued
      this.queued = null
      if (queued && this.hooks.ready()) void this.nudge(queued)
    })
    return this.running
  }

  private async run(full: boolean, signal: AbortSignal): Promise<void> {
    const workspaceId = await this.hooks.workspaceId()
    if (!workspaceId) return
    const state = this.hooks.readState(workspaceId)
    const { heads, cursor } = await this.hooks.pullHeads(workspaceId, full ? null : state.cursor)
    throwIfAborted(signal)

    const { live, trashed } = this.hooks.pages()
    const due = new Set<string>()
    // Pages left for later: skipped while in the Trash, or failed last time.
    // Purged pages drop out; restored and failed ones are caught up now.
    const deferred = new Set<string>()
    for (const id of state.deferred) {
      if (trashed.has(id)) deferred.add(id)
      else if (live.has(id)) due.add(id)
    }
    for (const head of heads) {
      if (trashed.has(head.id)) {
        deferred.add(head.id)
        continue
      }
      if (!live.has(head.id) || due.has(head.id)) continue
      const position = await this.hooks.localPosition(head.id)
      if (position && position.logId === head.logId && position.seq >= head.headSeq) continue
      due.add(head.id)
    }
    throwIfAborted(signal)

    const queue = [...due].filter((id) => !this.hooks.isOpen(id))
    const caughtUp: string[] = []
    const worker = async () => {
      while (queue.length > 0 && !signal.aborted) {
        const id = queue.shift()!
        try {
          await this.hooks.catchUp(id, signal)
          caughtUp.push(id)
        } catch (cause) {
          if (signal.aborted) return
          deferred.add(id)
          this.logFailure('Couldn’t bring a page up to date; it will be tried again', cause, id)
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker))
    throwIfAborted(signal)

    // Failed pages wait in `deferred`, so the cursor can move past them.
    this.hooks.writeState(workspaceId, { cursor, deferred: [...deferred] })
    if (caughtUp.length > 0) {
      syncLog.record({ direction: 'in', kind: 'page-catch-up', message: 'Brought closed pages up to date', targetType: 'page', targetIds: caughtUp, items: caughtUp.length })
      await this.hooks.caughtUp(caughtUp)
    }
  }

  private logFailure(message: string, cause: unknown, pageId?: string): void {
    syncLog.record({
      direction: 'in',
      level: 'warn',
      kind: 'page-catch-up-failed',
      message: `${message}: ${cause instanceof Error ? cause.message : String(cause)}`,
      ...(pageId ? { targetType: 'page' as const, targetIds: [pageId] } : {}),
    })
  }
}
